import React, { useMemo, useState } from "react";
import { Button, Flex, Select, Text, toast } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import {
  useBulkAssignTasksByFamilyMutation,
  GetWorkflowDocument,
} from "#graphql/supergraphHooks";
import { useFaUsers } from "../hooks/useFaUsers";
import { TaskType } from "../types";
import * as styles from "./styles_vanilla.css";
import type { TaskStepSummary } from "../types";

interface UnassignedOwnersBannerProps {
  workflowId: number;
  arenaId: number;
  taskSteps: TaskStepSummary[];
}

// Keep in sync with BE `_FA_LABELS` / `_IS_LABELS` in
// control_tower/services/client_team_assignment_service.py.
const FA_LABELS = new Set(["FA Preparer", "FA Reviewer", "FA Pool"]);
const IS_LABELS = new Set(["IS Primary", "IS Reviewer", "IS Pool"]);

export const UnassignedOwnersBanner: React.FC<UnassignedOwnersBannerProps> = ({
  workflowId,
  arenaId,
  taskSteps,
}) => {
  const { users, loading: usersLoading, error: usersError } = useFaUsers(arenaId);

  const { hasUnassignedFa, hasUnassignedIs, anyAssigned } = useMemo(() => {
    let fa = false;
    let is = false;
    // The banner is a bootstrap affordance for a brand-new workflow with no
    // assignments yet. As soon as any task carries an assignee, the workflow
    // has left bootstrap and remaining gaps are handled per-task via the picker.
    let assigned = false;
    for (const t of taskSteps) {
      if (t.assignee) assigned = true;
      if (t.taskType === TaskType.EXTERNAL_APPROVAL) continue;
      if (t.assignee) continue;
      const av = t.assignmentValue ?? "";
      if (FA_LABELS.has(av)) fa = true;
      else if (IS_LABELS.has(av)) is = true;
    }
    return { hasUnassignedFa: fa, hasUnassignedIs: is, anyAssigned: assigned };
  }, [taskSteps]);

  const [faUserId, setFaUserId] = useState<string>("");
  const [isUserId, setIsUserId] = useState<string>("");

  const [assign, { loading: submitting }] = useBulkAssignTasksByFamilyMutation({
    refetchQueries: [{ query: GetWorkflowDocument, variables: { id: workflowId } }],
    awaitRefetchQueries: true,
    onError: (e) => toast.error(e.message),
  });

  if (anyAssigned) return null;
  if (!hasUnassignedFa && !hasUnassignedIs) return null;

  const canApply = (hasUnassignedFa && !!faUserId) || (hasUnassignedIs && !!isUserId);

  const handleApply = (): void => {
    void assign({
      variables: {
        workflowId,
        faAssigneeId: faUserId || null,
        isAssigneeId: isUserId || null,
      },
      onCompleted: () => {
        setFaUserId("");
        setIsUserId("");
        toast.success("Owners assigned");
      },
    });
  };

  const placeholder = usersError
    ? "Failed to load users"
    : usersLoading
      ? "Loading users…"
      : "Select a user…";
  const selectsDisabled = submitting || !!usersError;

  return (
    <div className={styles.unassignedOwnersCard} data-testid="unassigned-owners-banner">
      <Flex alignItems="center" gap="8px" className={styles.unassignedOwnersHeader}>
        <Icon
          type="exclamation-triangle"
          variant="outline"
          color={Colors.Warning500}
          size="small"
        />
        <Text variant="strong-md">No default owners set for this workflow</Text>
      </Flex>
      <Flex flexDirection="column" gap="12px" className={styles.unassignedOwnersBody}>
        {hasUnassignedFa && (
          <Flex alignItems="center" gap="12px" className={styles.unassignedOwnersRow}>
            <Text variant="regular-md" className={styles.unassignedOwnersLabel}>
              Fund Administration
            </Text>
            <div className={styles.unassignedOwnersSelect}>
              <Select
                value={faUserId}
                onChange={(raw) => setFaUserId(String(raw))}
                disabled={selectsDisabled}
                label="Fund Administration owner"
                hideLabel
                placeholder={placeholder}
                position="down"
                matchFromStart={false}
                data-testid="unassigned-owners-fa-select"
              >
                <Select.Section>
                  {users.map((u) => (
                    <Select.Option key={u.id} value={u.id}>
                      {u.displayName}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
            </div>
          </Flex>
        )}
        {hasUnassignedIs && (
          <Flex alignItems="center" gap="12px" className={styles.unassignedOwnersRow}>
            <Text variant="regular-md" className={styles.unassignedOwnersLabel}>
              Investor Services
            </Text>
            <div className={styles.unassignedOwnersSelect}>
              <Select
                value={isUserId}
                onChange={(raw) => setIsUserId(String(raw))}
                disabled={selectsDisabled}
                label="Investor Services owner"
                hideLabel
                placeholder={placeholder}
                position="down"
                matchFromStart={false}
                data-testid="unassigned-owners-is-select"
              >
                <Select.Section>
                  {users.map((u) => (
                    <Select.Option key={u.id} value={u.id}>
                      {u.displayName}
                    </Select.Option>
                  ))}
                </Select.Section>
              </Select>
            </div>
          </Flex>
        )}
      </Flex>
      <Flex justifyContent="flex-end" className={styles.unassignedOwnersActions}>
        <Button
          variant="primary"
          disabled={!canApply || submitting}
          onClick={handleApply}
          data-testid="unassigned-owners-apply"
        >
          Apply
        </Button>
      </Flex>
    </div>
  );
};

export default UnassignedOwnersBanner;
